import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "tabcontent" }
const _hoisted_2 = { id: "date3_info" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "dropdown-arrow" }
const _hoisted_6 = { key: 1 }

import { reactive, onMounted, watch, computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import i18n from 'i18next';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { mapCountryByKey, mapEventByKey, loadWarDatabase, mapCategoryByKey } from '@/helpers/infoHelpers';

import { EventsModel, InfoStructure } from '@/models/sidebar/info.model';

import InfoCard from './InfoCard.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'Info',
  setup(__props) {

dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

const { locale, t } = useI18n();

const store = useStore();

const collapsedCategories = ref<Record<string, boolean>>({});

const currentDate = computed(() => store.state.date);

const state = reactive<InfoStructure>({
  dbSources: [],
  geos: [],
  events: [],
  dbCountries: [],
  dates: [],
  countries: [],
  sources: [],
  categories: [],
});

onMounted(async () => {
  await loadData();
  locale.value = store.state.locale;

  watch(() => store.state.war,
    async () => {
      await loadData();
    }
  );

  watch(locale, (newLocale, oldLocale) => {
    if (newLocale === oldLocale) {
      return;
    }
    store.commit('setLocale', newLocale);
    i18n.changeLanguage(newLocale);
  }, { immediate: true });

  watch(currentDate, () => {
    collapsedCategories.value = {};
  });

  watch(() => store.state.expandCategory, (categoryName) => {
    if (categoryName) {
      collapsedCategories.value[categoryName] = false;

      store.commit('setExpandCategory', null);
    }
  });
});

const loadData = async () => {
  // TODO: db may be passed down from a parent component instead
  const db = await loadWarDatabase(store.state.war);
  if (db) {
    state.sources = db.sources;
    state.geos = db.geo;
    state.events = db.events;
    state.countries = db.countries;
    state.dates = db.dates;
    state.categories = db.categories;

    // For non-event related data
    state.dbCountries = state.countries;
    state.dbSources = state.sources;
  }
};

const parseDate = (date: string) => {
  const newDate = dayjs.tz(date, 'Asia/Jerusalem');
  const day = newDate.date();
  const month = newDate.locale(store.state.locale).format('MMMM');
  const year = newDate.year();
  return `${day} ${month} ${year}`;
};

const infosRef = computed(() => {
  const filteredEvents = state.events.filter(
    (event) => event.date === store.state.date
  );

  const groupedEvents: Record<string, EventsModel[]> = {};

  filteredEvents.forEach((element) => {
    const eventCountry = state.countries.find(
      (country) =>
        country._id === element.country ||
        country.shortName === element.country ||
        country['shortName_' + store.state.locale] === element.country
    );

    const eventCategory = state.categories.find(
      (category) => category._id === element.category
    );

    if (eventCountry) {
      const mappedEvent: EventsModel = {
        ...element,
        country:
          mapCountryByKey(eventCountry, 'shortName', store.state.locale) ||
          mapCountryByKey(eventCountry, 'name', store.state.locale),
        flag: eventCountry.flag,
        text: mapEventByKey(element, 'text', store.state.locale) as string,
        category: eventCategory
          ? mapCategoryByKey(eventCategory, 'name', store.state.locale) as string
          : 'Other events',
      };

      const groupKey = mappedEvent.category as string;

      if (!groupedEvents[groupKey]) {
        groupedEvents[groupKey] = [];
      }

      groupedEvents[groupKey].push(mappedEvent);
    }
  });

  for (const cat in groupedEvents) {
    groupedEvents[cat].sort((a, b) => a.pageWeight - b.pageWeight);
  }

  const sortedGroups = Object.entries(groupedEvents).sort((a, b) => {
    const getWeight = (categoryName: string) => {
      if (categoryName === 'Other events') {
        return Infinity;
      }
      return (
        state.categories.find(
          (cat) => mapCategoryByKey(cat, 'name', store.state.locale) === categoryName
        )?.pageWeight ?? 0
      );
    };

    return getWeight(a[0]) - getWeight(b[0]);
  });

  return sortedGroups;
});

function toggleCategory(categoryName: string) {
  collapsedCategories.value[categoryName] = !collapsedCategories.value[categoryName];
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h4", _hoisted_2, [
      _createElementVNode("b", null, _toDisplayString(parseDate(_ctx.$store.state.date)), 1)
    ]),
    (!!infosRef.value && Object.keys(infosRef.value).length && state.sources.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(infosRef.value, ([categoryName, events], index) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: categoryName + index
            }, [
              categoryName
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["divider-with-text", { 'dark-mode': _unref(store).state.dark }]),
                    onClick: ($event: any) => (toggleCategory(categoryName))
                  }, [
                    _createElementVNode("span", null, [
                      _createElementVNode("h4", null, [
                        _createElementVNode("span", _hoisted_5, _toDisplayString(collapsedCategories.value[categoryName] ? '▸' : '▾'), 1),
                        _createElementVNode("b", null, _toDisplayString(categoryName), 1)
                      ])
                    ])
                  ], 10, _hoisted_4))
                : _createCommentVNode("", true),
              (!collapsedCategories.value[categoryName])
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(events, (info, index) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: info._id + index
                    }, [
                      _createVNode(InfoCard, {
                        _id: info._id,
                        country: info.country,
                        cssClass: info.cssClass,
                        flag: info.flag,
                        location: info.location,
                        source: info.source,
                        text: info.text,
                        sources: state.sources
                      }, null, 8, ["_id", "country", "cssClass", "flag", "location", "source", "text", "sources"]),
                      (events[index + 1] && info.category === events[index + 1].category)
                        ? (_openBlock(), _createElementBlock("hr", {
                            key: 0,
                            class: _normalizeClass(["info-divider", { 'dark-mode': _unref(store).state.dark }])
                          }, null, 2))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)('dates.no_events')), 1))
  ]))
}
}

})